/**
 * Build a CloudWatch Logs Insights deep-link filtered to a given traceId.
 *
 * Spec: dev/projects/sl2-sstv2-live-trace.md §4.3
 *
 * AWS encodes Logs Insights URLs with a quirky double-encoding scheme: the
 * query body is URI-component-encoded, then `*` is replaced for spaces and
 * `~` is used as the separator. We build a single canonical query that
 * filters by `traceId = <id>` across the most relevant SST V2 log groups.
 *
 * The final URL is region/account-aware; we read the region from the wfe's
 * environment (Vite env) and let CloudWatch infer the account from the
 * signed-in user.
 */

const DEFAULT_REGION = "ca-central-1"

/** Log groups that participate in the SST V2 ingestion pipeline. */
const SSTV2_LOG_GROUPS = [
  "/aws/lambda/sl2-app-fnSstv2SqsConsumer",
  "/aws/lambda/sl2-app-fnSqsConsumer",
  "/aws/lambda/sl2-app-fnDdbStreamPipelineMrt",
  "/aws/lambda/sl2-app-fnDdbStreamPipelineMEventAndSqs",
]

const cwEncode = (s: string): string =>
  encodeURIComponent(s).replace(/%/g, "$").replace(/\*/g, "$2A")

/**
 * Construct the deep-link. Defensive: returns "" if traceId is empty.
 */
export const buildCloudWatchInsightsUrl = (
  traceId: string,
  opts?: { region?: string; logGroups?: string[] },
): string => {
  if (!traceId) return ""
  const region = opts?.region ?? DEFAULT_REGION
  const logGroups = opts?.logGroups ?? SSTV2_LOG_GROUPS

  const queryString = `fields @timestamp, @message, @logStream
| filter traceId = "${traceId}"
| sort @timestamp asc
| limit 1000`

  // Per AWS console scheme: outer ~( ... ) with key~value pairs separated by ~.
  // Strings inside are encoded with our custom cwEncode then wrapped in single
  // quotes. Arrays use ~( 'a'~'b' ).
  const lgList = logGroups.map((lg) => `'${cwEncode(lg)}`).join("~")
  const encQuery = cwEncode(queryString)

  // The format below mirrors what the AWS console emits when you paste a
  // query and copy the URL.
  const fragment =
    `logsV2:logs-insights?queryDetail=` +
    `~(end~0~start~-3600` +
    `~timeType~'RELATIVE` +
    `~unit~'seconds` +
    `~editorString~'${encQuery}` +
    `~source~(${lgList}))`

  return `https://${region}.console.aws.amazon.com/cloudwatch/home?region=${region}#${fragment}`
}

/**
 * Build an X-Ray console deep-link for a given Root= id.
 * Returns "" if the id is empty/undefined.
 */
export const buildXrayTraceUrl = (
  xrayRootId: string | undefined,
  opts?: { region?: string },
): string => {
  if (!xrayRootId) return ""
  const region = opts?.region ?? DEFAULT_REGION
  return `https://${region}.console.aws.amazon.com/cloudwatch/home?region=${region}#xray:traces/${encodeURIComponent(xrayRootId)}`
}
